"use client";

import { useState, useEffect, useCallback } from "react";
import { usePathname } from "@/i18n/navigation";
import { Modal, TextInput, Button, Stack, Title, Text, Group, Badge, Box } from "@mantine/core";
import { IconMail, IconGift, IconX } from "@tabler/icons-react";
import { weeklyAnalysesClaim } from "@/lib/analysisCount";

const STORAGE_KEY = "exitIntentShown";
const COOLDOWN_HOURS = 72; // 3 days cooldown

// Pages where we should NEVER show the popup
const BLOCKED_PATHS = [
  "/virtual-makeover",
  "/best-makeup-colors-for",
  "/ai-hair-color-try-on",
  "/what-color-blush-should-i-wear",
  "/what-color-lipstick-should-i-wear",
  "/best-hair-colors-for-skin-tone",
  "/color-analysis-quiz",
  "/virtual-color-draping",
  "/analysis-wizard",
  "/view-color-analysis",
  "/advanced-draping-wizard",
  "/kibbe-body-type-test/wizard",
  "/checkout",
  "/api",
];

export default function ExitIntentPopup() {
  const [isOpen, setIsOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pathname = usePathname();

  const shouldShowPopup = useCallback(() => {
    if (typeof window === "undefined") return false;

    // Don't show on blocked paths (paid flows, analysis pages, etc.)
    if (BLOCKED_PATHS.some(blocked => pathname?.startsWith(blocked))) {
      return false;
    }

    // Don't show if already captured email
    if (localStorage.getItem("emailCaptured") === "true") {
      return false;
    }

    // Don't show if user has made a purchase
    if (localStorage.getItem("hasPurchased") === "true") {
      return false;
    }

    // Check cooldown
    const lastShown = localStorage.getItem(STORAGE_KEY);
    if (lastShown) {
      const hoursSinceShown = (Date.now() - parseInt(lastShown)) / (1000 * 60 * 60);
      if (hoursSinceShown < COOLDOWN_HOURS) {
        return false;
      }
    }

    return true;
  }, [pathname]);

  useEffect(() => {
    if (typeof window === "undefined") return;

    // Don't even set up listener on blocked paths
    if (BLOCKED_PATHS.some(blocked => pathname?.startsWith(blocked))) {
      return;
    }

    let hasTriggered = false;
    let mouseOutCount = 0;

    const handleMouseLeave = (e: MouseEvent) => {
      // Only trigger when mouse leaves through top of page (exit intent)
      // AND the mouse Y position is very close to 0 (actually leaving, not just moving to tabs)
      if (e.clientY <= 5 && e.clientY >= 0 && !hasTriggered && shouldShowPopup()) {
        mouseOutCount++;

        // Only show after user has "exited" twice (reduces false positives)
        if (mouseOutCount >= 2) {
          hasTriggered = true;
          setIsOpen(true);
          localStorage.setItem(STORAGE_KEY, Date.now().toString());
        }
      }
    };

    // Much longer delay - 15 seconds on page before we start listening
    const timeout = setTimeout(() => {
      document.addEventListener("mouseleave", handleMouseLeave);
    }, 15000);

    return () => {
      clearTimeout(timeout);
      document.removeEventListener("mouseleave", handleMouseLeave);
    };
  }, [shouldShowPopup, pathname]);

  const validateEmail = (email: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!validateEmail(email)) {
      setError("Please enter a valid email address");
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch("/api/capture-email", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, source: "exit-intent" }),
      });

      if (!response.ok) {
        throw new Error("Failed to save email");
      }

      localStorage.setItem("userEmail", email);
      localStorage.setItem("emailCaptured", "true");
      setIsSubmitted(true);

      // Close after showing success
      setTimeout(() => {
        setIsOpen(false);
      }, 2000);
    } catch (err) {
      setError("Something went wrong. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  if (!isOpen) return null;

  return (
    <Modal
      opened={isOpen}
      onClose={handleClose}
      centered
      size="md"
      withCloseButton={false}
      overlayProps={{ backgroundOpacity: 0.55, blur: 3 }}
      padding={0}
      radius="lg"
    >
      <Box
        style={{
          background: "linear-gradient(135deg, #7c3aed 0%, #a855f7 100%)",
          padding: "1.5rem",
          position: "relative",
        }}
      >
        <Button
          variant="subtle"
          color="white"
          size="xs"
          onClick={handleClose}
          style={{ position: "absolute", top: 8, right: 8 }}
          p={4}
        >
          <IconX size={18} />
        </Button>

        <Stack align="center" gap="xs">
          <IconGift size={48} color="white" />
          <Title order={2} c="white" ta="center">
            Wait! Don't miss out
          </Title>
          <Text c="white" ta="center" fz="sm" opacity={0.9}>
            Get your free color analysis guide before you go
          </Text>
        </Stack>
      </Box>

      <Box p="xl">
        {isSubmitted ? (
          <Stack align="center" gap="md">
            <Badge size="xl" color="green" variant="filled">
              Success!
            </Badge>
            <Text ta="center" fw={500}>
              Check your inbox for your free guide!
            </Text>
          </Stack>
        ) : (
          <form onSubmit={handleSubmit}>
            <Stack gap="md">
              <Text ta="center" fz="sm" c="dimmed">
                {weeklyAnalysesClaim()}. Get exclusive tips and a free color guide.
              </Text>

              <TextInput
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                error={error}
                size="md"
                leftSection={<IconMail size={18} />}
              />

              <Button type="submit" size="lg" color="violet" loading={isLoading} fullWidth>
                Send Me the Free Guide
              </Button>

              <Button variant="subtle" color="gray" size="xs" onClick={handleClose}>
                No thanks, I'll figure it out myself
              </Button>
            </Stack>
          </form>
        )}
      </Box>
    </Modal>
  );
}
